import * as angusClipper from 'js-angusj-clipper';
import sum from 'lodash/sum';
import omit from 'lodash/omit';
import proj4 from 'proj4';
// TODO how to do this properly? probably load the library into one of the contexts?
// TODO make this work for stacked app i.e. node.js
// create an instance of the library (usually only do this once in your app)
export let clipperLoaded;
angusClipper.loadNativeClipperLibInstanceAsync(angusClipper.NativeClipperLibRequestedFormat.WasmWithAsmJsFallback)
  .then(clipper => {
    clipperLoaded = clipper;
  });

export const isClipperLoaded = () => !!clipperLoaded;

export const proj4String = ([longitude, latitude]) => `+proj=lcc +lat_0=${latitude} +lat_1=${latitude + 0.1} +lat_2=${latitude - 0.1} +lon_0=${longitude} +units=m +no_defs +ellps=WGS84`;


// [multipolygons] -> [polygons]
// bonus deal with mapbox ._geometry and extra props
export const flattenFeatures = features => features.reduce(
    (newFeatures, feature) => {
      const geometry = feature.geometry || feature._geometry;
      const properties = omit(feature.properties, 'id');
      if (feature?.layer?.id && !feature.properties.fromLayer) {
        // eg 'landuse'
        let layerId = feature.layer.id;
        properties.fromLayer = layerId;
      }
      if (geometry.type === 'MultiPolygon') {
        const expanded = geometry.coordinates.map(rings => ({
         ...feature,
          type: 'Feature',
          properties,
          geometry: { type: 'Polygon', coordinates: rings }
        }));
        return [...newFeatures, ...expanded];
      } if (geometry.type === 'MultiLineString') {
        const expanded = geometry.coordinates.map(rings => ({
         ...feature,
         type: 'Feature',
          properties,
          geometry: { type: 'LineString', coordinates: rings }
        }));
        return [...newFeatures, ...expanded];
      } if (geometry.type === 'MultiPoint') {
        const expanded = geometry.coordinates.map(coordinates => ({
         ...feature,
         type: 'Feature',
          properties,
          geometry: { type: 'Point', coordinates }
        }));
        return [...newFeatures, ...expanded];
      } if (geometry.type === 'GeometryCollection') {
        const expanded = geometry.geometries.map(g => ({
         ...feature,
         type: 'Feature',
          properties,
          geometry: g
        }));
        return [...newFeatures, ...expanded];
      }
      return [
        ...newFeatures,
        { ...feature, type: 'Feature', properties, geometry }
      ];
    }, []
  );
  

// this file is both sketch/geo.js and gigeom/geo.js, TODO merge
export const getNthCoord = (feature, ix) => {
  switch (feature.geometry.type) {
    case 'Point':
      return feature.geometry.coordinates;
    case 'Polygon':
      return feature.geometry.coordinates[0][ix];
    case 'MultiPolygon':
      return feature.geometry.coordinates[0][0][ix];
    case 'MultiLineString':
      return feature.geometry.coordinates[0][ix];
    case 'LineString':
      return feature.geometry.coordinates[ix];
    default:
      return feature.geometry.coordinates[ix];
  }
};

// this file is both sketch/geo.js and gigeom/geo.js, TODO merge
export const getFirstCoord = feature => {
  return getNthCoord(feature, 0);
};


export const getProjected = (f, geoProject) => {
    switch (f.geometry.type) {
        case 'Polygon': return f.geometry.coordinates.map(ring => ring.map(geoProject.forward));
        case 'LineString': return f.geometry.coordinates.map(geoProject.forward);
        case 'Point': return geoProject.forward(f.geometry.coordinates);
        default:
        console.error(JSON.parse(JSON.stringify(f)));
        throw new Error(`Unsupported geometry type: ${f.geometry.type}`);
    }
};
  

// SEE StackPerformanceTest.js
// TODO use D3 https://github.com/d3/d3-polygon/blob/master/src/contains.js#L1
// OR js-angusj-clipper pointInPolygon
export const pointInPoly = (point, vs) => {
    const [x, y] = point;
    let inside = false;
    for (let j = 1, i = 0; j < vs.length; i = j++) {
      const [xi, yi] = vs[i];
      const [xj, yj] = vs[j];
      const intersect = ((yi > y) != (yj > y))
        && (x < ((xj - xi) * ((y - yi) / (yj - yi))) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };
  


const scalePath = path => path.map(([x, y]) => ({ x: x * 1024, y: y * 1024 }));
// SEE StackPerformanceTest.js
export const doPolyFeaturesOverlapAtAll = (f1, f2) => {
  // using gjk collision detection here is ~2 times faster (maybe more if using WASM)
  // than computing the intersection, and is a possible way to improve performance.
  // However, it only works for convex polygons, and we would have to check again for area for each collision
  if (!clipperLoaded) throw ('clipper not loaded');

  if (f1.geometry.type === 'Point') {
    return pointInPoly(f1._projected, f2._projected);
  }

  try {
    const scaledPaths1 = f1._projected.map(path => ({
      data: scalePath(path),
      closed: true
    }));

    const scaledPaths2 = f2._projected.map(path => ({
      data: scalePath(path)
    }));

    const overlappingPaths = clipperLoaded.clipToPaths({
      clipType: angusClipper.ClipType.Intersection,
      subjectInputs: scaledPaths1,
      clipInputs: scaledPaths2,
      subjectFillType: angusClipper.PolyFillType.NonZero
    });

    const overlapArea = sum(overlappingPaths.map(clipperLoaded.area));
    if (!overlapArea) return false;

    return overlapArea > 0;
    // const area1 = sum(scaledPaths1.map(p => clipperLoaded.area(p.data)));
    // const area2 = sum(scaledPaths2.map(p => clipperLoaded.area(p.data)));
    // return Math.abs(overlapArea / Math.min(area1, area2)) > 0.02;
  } catch (e) {
    // just don't stack if this doesn't work
    console.log(e);
    console.log(f1);
    console.log(f2);
    return false;
  }
};

const stretchBox = (bbox, [x, y]) => {
  if (bbox.minX === undefined) {
    bbox.minX = bbox.maxX = x;
    bbox.minY = bbox.maxY = y;
    return;
  }
  if (x < bbox.minX) bbox.minX = x;
  else if (x > bbox.maxX) bbox.maxX = x;
  if (y < bbox.minY) bbox.minY = y;
  else if (y > bbox.maxY) bbox.maxY = y;
};

export function traverseBoxOverCoords(coordinates, bbox) {
  if (typeof coordinates[0] === 'number') {
    stretchBox(bbox, coordinates);
  } else {
    coordinates.forEach(coord => traverseBoxOverCoords(coord, bbox));
  }
}

export const makeBBoxCoords = (coordinates, props = {}) => {
  traverseBoxOverCoords(coordinates, props);
  return props;
};